// The runtime bundle only contains echarts/core plus the charts/components
// registered below (roughly a third of the full library).
import type { ECharts, EChartsOption } from "echarts";

import { CustomChart, LineChart, ScatterChart } from "echarts/charts";
import {
  AxisPointerComponent,
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { useCallback, useEffect, useRef, useState } from "react";

// Everything any heartcore-chart-options builder uses. Adding a new option
// feature (e.g. markPoint) requires registering its component here, or the
// chart silently renders without it.
echarts.use([
  LineChart,
  ScatterChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  AxisPointerComponent,
  VisualMapComponent,
  MarkLineComponent,
  MarkAreaComponent,
  GraphicComponent,
  SVGRenderer,
]);

export type { EChartsOption };

export type DataZoomRange = { start: number; end: number };

// Persistent dashed ruler lines for grids without a drag handle — ECharts
// clears their native axis-pointer lines on mouse-leave, while graphics live
// outside its pointer state machine. Call whenever the x-mapping changes
// (value, zoom, resize). `force` opts single-grid charts in (Events bar).
function applyRulerGraphics(
  chart: ECharts,
  v: number,
  idsRef: { current: string[] },
  force = false,
  stroke = "#555",
): void {
  const opt = chart.getOption() as { grid?: unknown };
  const rawGrids = Array.isArray(opt.grid)
    ? (opt.grid as Array<Record<string, unknown>>)
    : [];
  // Skip single-grid charts unless explicitly opted in. Those charts use a
  // native handle to keep the line alive; adding a graphic creates a duplicate.
  if (!force && rawGrids.length <= 1) return;

  const px = chart.convertToPixel({ xAxisIndex: 0 }, v);
  if (typeof px !== "number" || !Number.isFinite(px)) return;

  const chartH = chart.getHeight();
  const ids: string[] = [];
  // Multi-grid: draw on every grid except the last (which owns the handle).
  // Forced single-grid: draw on the one grid.
  const gridsToDraw = rawGrids.length > 1 ? rawGrids.slice(0, -1) : rawGrids;
  const lines = gridsToDraw.map((g, i) => {
    const id = `ruler-grid-${i}`;
    ids.push(id);
    const top = typeof g.top === "number" ? g.top : 0;
    const bottom =
      typeof g.height === "number"
        ? top + g.height
        : typeof g.bottom === "number"
          ? chartH - g.bottom
          : chartH;
    return {
      id,
      type: "line" as const,
      shape: { x1: px, y1: top, x2: px, y2: bottom },
      style: { stroke, lineWidth: 2, lineDash: [4, 3] },
      silent: true,
      z: 10,
    };
  });
  idsRef.current = ids;
  chart.setOption({ graphic: lines });
}

export function EChartsChart(props: {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  // Changing it re-initialises the chart; the state effects below re-apply
  // the current option/pointer/visibility/zoom to the fresh instance.
  overflowVisible?: boolean;
  // ---- shared axis pointer ------------------------------------------------
  // Drives the x-axis pointer to this timestamp (ms). Paired with
  // onAxisPointerUpdate to sync sibling charts without `echarts.connect()`,
  // which would also sync tooltips.
  axisPointerValue?: number | null;
  onAxisPointerUpdate?: (axisValueMs: number | null) => void;
  // ---- shared zoom range (percentages 0–100) ------------------------------
  dataZoomRange?: DataZoomRange | null;
  onDataZoomChange?: (range: DataZoomRange) => void;
  // ---- external series-visibility control --------------------------------
  // Maps series name → visible. Applied via legendSelect/legendUnSelect, so
  // the chart option must include a `legend` component (visible or not).
  selectedSeries?: Record<string, boolean>;
  // Opts a single-grid chart with no drag handle (e.g. the Events bar) into
  // the persistent graphic ruler — see applyRulerGraphics.
  persistGraphicRuler?: boolean;
  // Stroke for the graphic ruler. Pass the option's native axis-pointer colour
  // so the two read identically (the default grey vanishes on dark themes).
  rulerColor?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ECharts | null>(null);
  // Also held as state so the apply-effects below key on it: a re-init hands
  // them a new identity and they re-apply option/pointer/visibility/zoom —
  // with only the ref, the chart would come back blank until a prop changed.
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);

  const axisPointerCallbackRef = useRef(props.onAxisPointerUpdate);
  axisPointerCallbackRef.current = props.onAxisPointerUpdate;
  const dataZoomCallbackRef = useRef(props.onDataZoomChange);
  dataZoomCallbackRef.current = props.onDataZoomChange;

  // Always-current copies for use inside stable callbacks (ResizeObserver,
  // datazoom event) that can't depend on the React render cycle.
  const axisPointerValueRef = useRef(props.axisPointerValue);
  axisPointerValueRef.current = props.axisPointerValue;
  const persistGraphicRulerRef = useRef(props.persistGraphicRuler);
  persistGraphicRulerRef.current = props.persistGraphicRuler;
  const rulerColorRef = useRef(props.rulerColor);
  rulerColorRef.current = props.rulerColor;

  // Loop-prevention refs: when we programmatically dispatch
  // updateAxisPointer / dataZoom, ECharts re-fires the matching event. For
  // axisPointer we count expected echoes (multi-grid charts dispatch once
  // per xAxis); for zoom we compare values.
  const axisPointerEchoCountRef = useRef(0);
  const lastDispatchedZoomRef = useRef<DataZoomRange | null>(null);
  // The last range this chart emitted from a user drag. When the parent lifts it
  // and feeds it back via `dataZoomRange`, we must NOT re-dispatch it — doing so
  // fights the in-progress drag and makes the slider jitter back and forth.
  const lastEmittedZoomRef = useRef<DataZoomRange | null>(null);
  // Whatever range this chart currently shows, from any source (user drag,
  // external dispatch). Re-applied after a notMerge setOption, which would
  // otherwise reset the zoom to the full range (e.g. on a theme toggle).
  const currentZoomRef = useRef<DataZoomRange | null>(null);
  const rulerGraphicIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = echarts.init(container, undefined, { renderer: "svg" });
    chartRef.current = chart;
    setChartInstance(chart);

    if (props.overflowVisible) {
      const viewport = container.firstElementChild as HTMLElement | null;
      if (viewport) viewport.style.overflow = "visible";
    }

    chart.on("updateAxisPointer", (event: unknown) => {
      // Skip echoes from our own programmatic dispatches (one per xAxis in
      // multi-grid charts). Skipping here prevents both a tooltip-flicker
      // cascade and a re-emit loop back to the parent.
      if (axisPointerEchoCountRef.current > 0) {
        axisPointerEchoCountRef.current--;
        return;
      }
      // Suppress any tooltip ECharts may auto-show while the user is moving
      // the axis pointer (handle drag, click reposition). The per-point hover
      // tooltip re-shows on mousemove afterward.
      chart.dispatchAction({ type: "hideTip" });
      const e = event as { axesInfo?: { axisDim?: string; value?: number }[] };
      const xInfo = e.axesInfo?.find((a) => a.axisDim === "x");
      const v = xInfo?.value;
      // Don't emit null (e.g. on mouse-leave): axes without a handle fire leave
      // events even when a sibling handle-axis is holding its position. Emitting
      // null would clear the shared ruler state and make those lines disappear.
      if (typeof v !== "number") return;
      axisPointerCallbackRef.current?.(v);
    });

    chart.on("datazoom", (event: unknown) => {
      const e = event as {
        start?: number;
        end?: number;
        batch?: { start?: number; end?: number }[];
      };
      const first = e.batch?.[0] ?? e;
      const start = typeof first.start === "number" ? first.start : null;
      const end = typeof first.end === "number" ? first.end : null;
      if (start === null || end === null) return;
      // Track the shown range regardless of source (drag or echo) so it can be
      // restored after a notMerge option rebuild.
      currentZoomRef.current = { start, end };
      const last = lastDispatchedZoomRef.current;
      if (
        last &&
        Math.abs(last.start - start) < 0.5 &&
        Math.abs(last.end - end) < 0.5
      ) {
        lastDispatchedZoomRef.current = null;
        return;
      }
      lastEmittedZoomRef.current = { start, end };
      dataZoomCallbackRef.current?.({ start, end });
    });

    const observer = new ResizeObserver(() => {
      chart.resize();
      // After resize the x-coordinate mapping changes — reposition any graphic
      // ruler lines so they stay aligned with their timestamp.
      const v = axisPointerValueRef.current;
      if (v != null)
        applyRulerGraphics(
          chart,
          v,
          rulerGraphicIdsRef,
          persistGraphicRulerRef.current,
          rulerColorRef.current,
        );
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [props.overflowVisible]);

  // Drive the native axis pointer(s) + graphic ruler lines to timestamp `v`.
  // Shared by the axisPointerValue effect and the option effect below — a
  // notMerge setOption resets ECharts' pointer state and graphics, so the
  // ruler must be re-applied after every option rebuild. Only refs inside, so
  // the callback is stable.
  const applyAxisPointer = useCallback((chart: ECharts, v: number) => {
    // Draw/reposition graphic ruler lines for non-handle grids (multi-grid only).
    applyRulerGraphics(
      chart,
      v,
      rulerGraphicIdsRef,
      persistGraphicRulerRef.current,
      rulerColorRef.current,
    );

    const px = chart.convertToPixel({ xAxisIndex: 0 }, v);
    if (typeof px !== "number" || !Number.isFinite(px)) return;

    const opt = chart.getOption() as { xAxis?: unknown };
    const xAxisCount = Array.isArray(opt.xAxis) ? opt.xAxis.length : 1;
    const h = chart.getHeight();
    axisPointerEchoCountRef.current = xAxisCount;
    for (let i = 0; i < xAxisCount; i++) {
      const yI = (h / xAxisCount) * (i + 0.5);
      chart.dispatchAction({
        type: "updateAxisPointer",
        currTrigger: "click",
        x: px,
        y: yI,
      });
    }
  }, []);

  useEffect(() => {
    const chart = chartInstance;
    if (!chart) return;
    // Synchronous (no lazyUpdate): the re-applies below need the new option's
    // coordinate system to be in effect.
    chart.setOption(props.option, { notMerge: true });
    // notMerge resets dataZoom, axis-pointer state and graphic rulers. Restore
    // the current interaction state so an option rebuild (theme toggle, data
    // refresh) doesn't visibly reset the zoom window or the shared ruler.
    const zoom = currentZoomRef.current;
    if (zoom) {
      lastDispatchedZoomRef.current = zoom;
      chart.dispatchAction({
        type: "dataZoom",
        start: zoom.start,
        end: zoom.end,
        dataZoomIndex: 0,
      });
    }
    const v = axisPointerValueRef.current;
    if (v != null) applyAxisPointer(chart, v);
  }, [chartInstance, props.option, applyAxisPointer]);

  // Apply external axis pointer value.
  useEffect(() => {
    const chart = chartInstance;
    const v = props.axisPointerValue;
    if (!chart) return;

    if (v == null) {
      axisPointerEchoCountRef.current = 0;
      chart.dispatchAction({ type: "updateAxisPointer", currTrigger: "leave" });
      const ids = rulerGraphicIdsRef.current;
      if (ids.length > 0) {
        chart.setOption({
          graphic: ids.map((id) => ({ id, $action: "remove" as const })),
        });
        rulerGraphicIdsRef.current = [];
      }
      return;
    }

    applyAxisPointer(chart, v);
  }, [chartInstance, props.axisPointerValue, applyAxisPointer]);

  // Apply external series-visibility map.
  useEffect(() => {
    const chart = chartInstance;
    const sel = props.selectedSeries;
    if (!chart || !sel) return;
    for (const [name, visible] of Object.entries(sel)) {
      chart.dispatchAction({
        type: visible ? "legendSelect" : "legendUnSelect",
        name,
      });
    }
  }, [chartInstance, props.selectedSeries, props.option]);

  // Apply external zoom range.
  useEffect(() => {
    const chart = chartInstance;
    const r = props.dataZoomRange;
    if (!chart || !r) return;
    // Ignore the value echoing back from this chart's own drag — it's already
    // there, and re-dispatching mid-drag causes the slider to jump/jitter.
    const emitted = lastEmittedZoomRef.current;
    if (
      emitted &&
      Math.abs(emitted.start - r.start) < 0.5 &&
      Math.abs(emitted.end - r.end) < 0.5
    ) {
      return;
    }
    lastDispatchedZoomRef.current = r;
    currentZoomRef.current = r;
    chart.dispatchAction({
      type: "dataZoom",
      start: r.start,
      end: r.end,
      dataZoomIndex: 0,
    });
    // Zoom changes the x-coordinate mapping — reposition graphic ruler lines.
    const v = axisPointerValueRef.current;
    if (v != null)
      applyRulerGraphics(
        chart,
        v,
        rulerGraphicIdsRef,
        persistGraphicRulerRef.current,
        rulerColorRef.current,
      );
  }, [chartInstance, props.dataZoomRange]);

  return (
    <div ref={containerRef} className={props.className} style={props.style} />
  );
}
